import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import "./themeupdate.css";

import { fetchThemeData } from "../../themeSlice";
import {
  selectAdminToken,
  selectOrganizationId,
  selectSessionId,
} from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { ensureThemeJsonArray } from "../../../functions/themeAssets";
import { clampQuizFields, validateQuizList } from "./quizHelpers";
import QuizTab from "./QuizTab";
import ThemeUpdateFeatureTour, {
  markThemeUpdateTourSeen,
  shouldAutoStartThemeUpdateTour,
} from "./ThemeUpdateFeatureTour";

let pairIdCounter = 0;
function newPairId(prefix = "pair") {
  pairIdCounter += 1;
  return `${prefix}-${pairIdCounter}`;
}

function normalizeQuizFromTheme(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return list.map((item) => ({
    id: newPairId("quiz"),
    ...clampQuizFields(item),
  }));
}

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const navigate = useNavigate();
  const { currentTheme, data, status } = useSelector((state) => state.theme);
  const categories = useMemo(() => ensureThemeJsonArray(data?.categories), [data]);

  const [isSaving, setIsSaving] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizErrorMessage, setQuizErrorMessage] = useState("");
  const [quizSuccessMessage, setQuizSuccessMessage] = useState("");
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setQuizQuestions(normalizeQuizFromTheme(data.quiz));
  }, [data]);

  useEffect(() => {
    if (!data) return;
    if (!shouldAutoStartThemeUpdateTour()) return;
    const t = window.setTimeout(() => setTourOpen(true), 400);
    return () => window.clearTimeout(t);
  }, [data]);

  const closeTour = useCallback(() => {
    markThemeUpdateTourSeen();
    setTourOpen(false);
  }, []);

  const persistQuizQuestions = async (nextQuestions) => {
    const validation = validateQuizList(nextQuestions.map(({ id, ...rest }) => rest));
    if (!validation.ok) {
      throw new Error(validation.message);
    }

    setIsSaving(true);
    setQuizErrorMessage("");
    setQuizSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            quiz: validation.data,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      setQuizQuestions(
        nextQuestions.map((q, i) => ({
          id: q.id || newPairId("quiz"),
          ...validation.data[i],
        }))
      );
    } catch (err) {
      setQuizErrorMessage(err?.message || "Failed to save questions to theme.");
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading theme data…</div>
      </div>
    );
  }

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <header className="themeupdate-header themeupdate-panel-wide" data-tour="tour-gc-header">
        <h1 className="themeupdate-title">Game Content</h1>
        <p className="themeupdate-muted">
          Add questions for this Snakes and Ladders theme. Questions are stored on the theme, not in
          a separate questions table.
        </p>
      </header>

      <QuizTab
        questions={quizQuestions}
        setQuestions={setQuizQuestions}
        categories={categories}
        isSaving={isSaving}
        onPersist={persistQuizQuestions}
        errorMessage={quizErrorMessage}
        successMessage={quizSuccessMessage}
        setErrorMessage={setQuizErrorMessage}
        setSuccessMessage={setQuizSuccessMessage}
        newQuestionId={() => newPairId("quiz")}
      />

      <ThemeUpdateFeatureTour open={tourOpen} onClose={closeTour} />
    </div>
  );
};

export default ThemeUpdate;
